import React, { useState, useMemo } from "react";
import PageBanner from "../Common/PageBanner";
import ScrollReveal from "../Common/ScrollReveal";
import { useGetInvestorSectionsQuery } from "../../store/investorApi";
import BASE_URL from "../../Services/BASE_URL";
import "./InvestorRelations.css";

const currentYear = new Date().getFullYear();

const STATIC_SIDEBAR_LINKS = [
  { id: "investor-contact", label: "Investor Contact" },
  { id: "policies-and-codes", label: "Policies and Codes" },
  { id: "disclosures-under-regulation-46-of-sebi-lodr", label: "Disclosures under Regulation 46 of SEBI LODR" },
  { id: "regulations", label: "Regulations" },
  { id: "press-release", label: "Press Release" },
];

const STATIC_CONTENT = {
  "investor-contact": {
    title: "Investor Contact",
    body: (
      <>
        <p>For investor inquiries, please reach out to our dedicated Investor Relations team.</p>
        <p><strong>Email:</strong> investor.relations@pawansut.com</p>
        <p><strong>Phone:</strong> +91 (XXX) XXX-XXXX</p>
        <p><strong>Address:</strong> Corporate Office, Pawansut Holdings Limited. (Dummy address for display.)</p>
      </>
    ),
  },
  "policies-and-codes": {
    title: "Policies and Codes",
    body: (
      <>
        <p>We adhere to comprehensive policies and codes of conduct to ensure transparency and ethical practices.</p>
        <p>Our key policies include Code of Conduct, Insider Trading Policy, and related governance documents. This section will be updated with actual policy documents and links.</p>
      </>
    ),
  },
  "disclosures-under-regulation-46-of-sebi-lodr": {
    title: "Disclosures under Regulation 46 of SEBI LODR",
    body: (
      <>
        <p>In compliance with Regulation 46 of the SEBI (Listing Obligations and Disclosure Requirements) Regulations, 2015, we make the following disclosures available to our shareholders and the public.</p>
        <p>Disclosure content and documents will be published here. This is placeholder content for now.</p>
      </>
    ),
  },
  regulations: {
    title: "Regulations",
    body: (
      <>
        <p>Our operations are guided by applicable regulatory frameworks including SEBI regulations, Companies Act, and sector-specific guidelines.</p>
        <p>Relevant regulations and compliance information will be listed in this section. Dummy content for display.</p>
      </>
    ),
  },
  "press-release": {
    title: "Press Release",
    body: (
      <>
        <p>Latest press releases and official announcements from Pawansut Holdings Limited.</p>
        <p>Press release listings and archives will appear here. Placeholder content for now.</p>
      </>
    ),
  },
};

const InvestorRelations = () => {
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedStaticKey, setSelectedStaticKey] = useState(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { data: sectionsData, isLoading, isError } = useGetInvestorSectionsQuery();

  const sections = useMemo(() => {
    if (!sectionsData || !Array.isArray(sectionsData)) return [];
    return [...sectionsData].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [sectionsData]);

  const allYears = useMemo(() => {
    const years = new Set();
    sections.forEach((section) => {
      (section.links || []).forEach((link) => {
        (link.documents || []).forEach((doc) => {
          if (doc.year) years.add(doc.year);
        });
      });
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [sections]);

  const availableYears = allYears.length > 0 ? allYears : [currentYear];
  const yearFilter = availableYears.includes(selectedYear) ? selectedYear : availableYears[0];

  const toggleDropdown = (sectionId) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleLinkClick = (link) => {
    setSelectedLink(link);
    setSelectedStaticKey(null);
  };

  const handleStaticLinkClick = (id) => {
    setSelectedStaticKey(id);
    setSelectedLink(null);
  };

  const documentsForSelectedLink = useMemo(() => {
    if (!selectedLink || !selectedLink.documents) return [];
    return selectedLink.documents.filter((doc) => doc.year === yearFilter);
  }, [selectedLink, yearFilter]);

  const getDocumentUrl = (doc) => {
    const url = doc.filePath || doc.fileUrl || doc.documentUrl || doc.url || doc.path || doc.file;
    if (!url) return null;
    if (typeof url === "string" && (url.startsWith("http://") || url.startsWith("https://"))) return url;
    const base = BASE_URL.replace(/\/$/, "");
    const path = typeof url === "string" ? url : url?.url || url?.path;
    if (!path) return null;
    return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
  };

  return (
    <div className="investor-relations-page">
      <PageBanner title="Investor Relations" />

      {/* Year filter bar - only when a dropdown link is selected (doc list shown) */}
      {selectedLink && (
        <div className="investor-relations-year-bar">
          <div className="investor-relations-year-bar-inner">
            <label htmlFor="investor-year-filter" className="investor-relations-year-label">
              Year:
            </label>
            <select
              id="investor-year-filter"
              className="investor-relations-year-select"
              value={yearFilter}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <ScrollReveal variant="fadeUp">
        <section className="investor-relations-main-section unique-grid-bg">
          <div className="investor-relations-layout-wrapper">
            {/* Left Sidebar - dynamic from API */}
            <aside className="investor-relations-sidebar">
              {isLoading ? (
                <div className="investor-relations-sidebar-loading">Loading...</div>
              ) : isError ? (
                <div className="investor-relations-sidebar-error">Failed to load menu.</div>
              ) : (
                <nav className="investor-relations-sidebar-nav">
                  {sections.map((section) => {
                    const links = section.links || [];
                    const hasLinks = links.length > 0;
                    const isOpen = openDropdowns[section.id];

                    return (
                      <div key={section.id} className="investor-relations-menu-item-wrapper">
                        <div
                          className={`investor-relations-menu-item ${selectedLink && selectedLink.sectionId === section.id ? "active" : ""}`}
                          onClick={() => hasLinks && toggleDropdown(section.id)}
                        >
                          <span className="investor-relations-menu-text">{section.title}</span>
                          {hasLinks && (
                            <span className={`investor-relations-chevron ${isOpen ? "open" : ""}`}>
                              {isOpen ? (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              ) : (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </span>
                          )}
                        </div>
                        {hasLinks && isOpen && (
                          <div className="investor-relations-dropdown">
                            <ul className="investor-relations-dropdown-list">
                              {[...(links || [])]
                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                .map((link) => (
                                  <li
                                    key={link.id}
                                    className={`investor-relations-dropdown-item ${selectedLink?.id === link.id ? "active" : ""}`}
                                    onClick={() => handleLinkClick(link)}
                                  >
                                    <span className="investor-relations-dropdown-line"></span>
                                    <span className="investor-relations-dropdown-text">{link.title}</span>
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Static links after dynamic dropdowns */}
                  {STATIC_SIDEBAR_LINKS.map((item) => (
                    <div key={item.id} className="investor-relations-menu-item-wrapper">
                      <div
                        role="button"
                        tabIndex={0}
                        className={`investor-relations-menu-item investor-relations-static-link ${selectedStaticKey === item.id ? "active" : ""}`}
                        onClick={() => handleStaticLinkClick(item.id)}
                        onKeyDown={(e) => e.key === "Enter" && handleStaticLinkClick(item.id)}
                      >
                        <span className="investor-relations-menu-text">{item.label}</span>
                      </div>
                    </div>
                  ))}
                </nav>
              )}
            </aside>

            {/* Right Content Area - documents for selected link or static content */}
            <div className="investor-relations-content-area">
              {selectedLink ? (
                <div className="investor-relations-docs-section">
                  <h2 className="investor-relations-main-title">{selectedLink.title}</h2>
                  {documentsForSelectedLink.length === 0 ? (
                    <p className="investor-relations-no-docs">No documents for {yearFilter}.</p>
                  ) : (
                    <ul className="investor-relations-doc-list">
                      {documentsForSelectedLink.map((doc) => {
                        const docUrl = getDocumentUrl(doc);
                        const name = doc.displayName || doc.title || `Document ${doc.id}`;
                        return (
                          <li key={doc.id} className="investor-relations-doc-item">
                            <span className="investor-relations-doc-name">{name}</span>
                            <div className="investor-relations-doc-actions">
                              {docUrl ? (
                                <>
                                  <a
                                    href={docUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="investor-relations-doc-btn investor-relations-doc-btn-view"
                                    aria-label={`View ${name}`}
                                  >
                                    View
                                  </a>
                                  <a
                                    href={docUrl}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="investor-relations-doc-btn investor-relations-doc-btn-download"
                                    aria-label={`Download ${name}`}
                                  >
                                    Download
                                  </a>
                                </>
                              ) : (
                                <span className="investor-relations-doc-unavailable">No file link</span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : selectedStaticKey && STATIC_CONTENT[selectedStaticKey] ? (
                <div className="investor-relations-docs-section investor-relations-static-content">
                  <h2 className="investor-relations-main-title">{STATIC_CONTENT[selectedStaticKey].title}</h2>
                  <div className="investor-relations-description">{STATIC_CONTENT[selectedStaticKey].body}</div>
                </div>
              ) : (
                <div className="investor-relations-text-section">
                  <h2 className="investor-relations-main-title">Building Trust Through Transparency</h2>
                  <h3 className="investor-relations-company-title">About Our Investor Relations</h3>
                  <div className="investor-relations-description">
                    <p>
                      At Pawansut Holdings Limited, we are committed to maintaining the highest standards of transparency and communication with our investors. Our Investor Relations team works diligently to provide timely, accurate, and comprehensive information about our financial performance, strategic initiatives, and corporate governance.
                    </p>
                    <p>
                      We believe in fostering strong relationships with our shareholders, analysts, and the investment community. Through regular updates, financial reports, and open dialogue, we ensure that all stakeholders have access to the information they need to make informed decisions.
                    </p>
                  </div>
                  <div className="investor-relations-features">
                    <h3>Key Highlights:</h3>
                    <ul className="feature-list">
                      <li><span className="feature-icon">📊</span> Regular Financial Reports</li>
                      <li><span className="feature-icon">📈</span> Transparent Performance Metrics</li>
                      <li><span className="feature-icon">🤝</span> Strong Corporate Governance</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Information Section */}
        <section className="investor-relations-info-section">
          <div className="investor-relations-info-content">
            <div className="investor-relations-info-text">
              <h2>Financial Information</h2>
              <p>
                We provide comprehensive financial information including annual reports, quarterly earnings, and other regulatory filings. Our commitment to transparency ensures that investors have access to all relevant financial data and performance metrics.
              </p>
              <p>
                For detailed financial information, annual reports, and investor presentations, please contact our Investor Relations team. We are here to answer any questions and provide the information you need.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="investor-contact" className="investor-relations-cards-section">
          <div className="investor-relations-cards-grid">
            <div className="investor-relations-card">
              <div className="investor-relations-card-icon">
                <svg width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="20" stroke="#1565c0" strokeWidth="2.5"/><path d="M24 14v10l7 7" stroke="#1565c0" strokeWidth="2.5" strokeLinecap="round"/></svg>
              </div>
              <h3>Contact Investor Relations</h3>
              <p>For investor inquiries, please reach out to our dedicated Investor Relations team who are available to assist you with any questions or information requests.</p>
            </div>
            <div className="investor-relations-card">
              <div className="investor-relations-card-icon">
                <svg width="48" height="48" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="8" y="12" width="32" height="24" rx="4" stroke="#00BFAE" strokeWidth="2.5"/><path d="M16 20h16M16 24h8" stroke="#00BFAE" strokeWidth="2.5" strokeLinecap="round"/></svg>
              </div>
              <h3>Stay Informed</h3>
              <p>Subscribe to our investor updates and receive timely notifications about financial reports, corporate announcements, and important company news.</p>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </div>
  );
};

export default InvestorRelations;
